import clsx from 'clsx'
import { useEffect, useState } from 'react'
import '../../styles/slp.css'

export type DeviceKind = 'audioinput' | 'audiooutput' | 'videoinput'

const FALLBACK_NAME: Record<DeviceKind, string> = {
  audioinput: 'Microphone',
  audiooutput: 'Speaker',
  videoinput: 'Camera',
}

/**
 * SLP device dropdown — enumerates real devices of one kind via
 * `navigator.mediaDevices.enumerateDevices()` and shows their actual names
 * (Focusrite / U-T-VID capture interfaces etc. appear verbatim). Labels are
 * empty until the user has granted a matching media permission; we then fall
 * back to "Microphone 2"-style names. Re-enumerates on `devicechange`.
 */
export function DeviceSelect({
  kind,
  value,
  onChange,
  label,
  disabled,
  className,
  testId,
}: {
  kind: DeviceKind
  /** Selected deviceId (uncontrolled default = first device). */
  value?: string
  onChange?: (deviceId: string, device: MediaDeviceInfo | undefined) => void
  /** Visible row label, e.g. "Audio Input". Defaults per kind. */
  label?: string
  disabled?: boolean
  className?: string
  /** Override data-testid (default `rt-device-${kind}`) — needed when two
   *  selects of the same kind share a page (e.g. Program audio source). */
  testId?: string
}) {
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
  const [unsupported, setUnsupported] = useState(false)

  useEffect(() => {
    const md = navigator.mediaDevices
    if (!md?.enumerateDevices) {
      setUnsupported(true)
      return
    }
    let cancelled = false
    const refresh = () => {
      md.enumerateDevices()
        .then((all) => {
          if (!cancelled) setDevices(all.filter((d) => d.kind === kind))
        })
        .catch(() => {
          if (!cancelled) setDevices([])
        })
    }
    refresh()
    md.addEventListener?.('devicechange', refresh)
    return () => {
      cancelled = true
      md.removeEventListener?.('devicechange', refresh)
    }
  }, [kind])

  const fallback = FALLBACK_NAME[kind]
  return (
    <label className={clsx('flex items-center gap-2 text-[13px] text-[var(--slp-text)]', className)}>
      <span className="w-24 shrink-0 text-[var(--slp-text-dim)]">
        {label ?? (kind === 'audioinput' ? 'Audio Input' : kind === 'audiooutput' ? 'Audio Output' : 'Video Input')}
      </span>
      <select
        data-testid={testId ?? `rt-device-${kind}`}
        disabled={disabled || unsupported || devices.length === 0}
        value={value}
        onChange={(e) =>
          onChange?.(
            e.target.value,
            devices.find((d) => d.deviceId === e.target.value),
          )
        }
        className={clsx(
          'min-w-0 flex-1 rounded border border-[var(--slp-border)] bg-[var(--slp-panel)]',
          'px-2 py-1 text-[13px] text-[var(--slp-text)]',
          'focus:border-[var(--slp-blue)] focus:outline-none',
          'disabled:bg-[var(--slp-panel-2)] disabled:text-[var(--slp-text-dim)]',
        )}
      >
        {devices.length === 0 ? (
          <option value="">{unsupported ? 'Not supported' : `No ${fallback.toLowerCase()} found`}</option>
        ) : (
          devices.map((d, i) => (
            <option key={d.deviceId || i} value={d.deviceId}>
              {d.label || `${fallback} ${i + 1}`}
            </option>
          ))
        )}
      </select>
    </label>
  )
}
